/**
 * The website check, on the page - Craig's ruling C, 25 September 2026.
 *
 * **The server is the one that counts**, as always: `businessWebsiteSchema`
 * refuses the same answers on every request, so a stale bundle changes nothing.
 * This exists so the buyer is told before they press the button, with the same
 * sentence, rather than filling a form in to be corrected at the end of it.
 *
 * Both lists and all three messages are restated from
 * `server/src/modules/agencies/business.ts`, and `businessWebsite.test.ts`
 * reads that file off disk and fails on any drift. Two lists kept in step by a
 * comment asking the next person is the shape this repository has stopped
 * writing.
 */

/** A platform's front door is somebody naming the site, not their page on it. */
const PLATFORM_HOSTS = [
  'linkedin.com',
  'facebook.com',
  'instagram.com',
  'x.com',
  'twitter.com',
  'tiktok.com',
  'youtube.com',
];

/** What somebody types when they are typing the example rather than an answer. */
const PLACEHOLDER_HOSTS = [
  'example.com',
  'example.org',
  'example.net',
  'website.com',
  'mywebsite.com',
  'yourwebsite.com',
  'domain.com',
  'test.com',
  'company.com',
  'business.com',
  // The example the form shows since 29 September: copying it is not an answer.
  'yourbusiness.com',
];

export const WEBSITE_PROBLEMS = {
  platform_home: 'Add the link to your own page, for example linkedin.com/company/your-business.',
  placeholder: 'Enter your own business website or profile.',
  malformed: 'Enter a web address, like yourbusiness.com.',
} as const;

export type WebsiteProblem = keyof typeof WEBSITE_PROBLEMS;

function parseWebsite(raw: string): URL | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const withScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) ? trimmed : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (!url.hostname.includes('.')) return null;
  if (url.hostname.endsWith('.')) return null;

  return url;
}

/** What is wrong with this answer, or `null` when nothing is. */
export function websiteProblem(raw: string): WebsiteProblem | null {
  const url = parseWebsite(raw);
  if (url === null) return 'malformed';

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const hasPath = url.pathname !== '' && url.pathname !== '/';

  if (PLATFORM_HOSTS.includes(host) && !hasPath) return 'platform_home';
  if (PLACEHOLDER_HOSTS.includes(host)) return 'placeholder';

  return null;
}

/** The message for whatever is wrong, or `undefined`. For a form field. */
export function websiteError(raw: string): string | undefined {
  // An empty box is not an error yet: the buyer has not finished. The button
  // stays disabled, and the server refuses it if they get past that.
  if (raw.trim() === '') return undefined;

  const problem = websiteProblem(raw);
  return problem === null ? undefined : WEBSITE_PROBLEMS[problem];
}
